import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  listPhoneNumbers,
  generatePhoneNumber,
  getPhoneNumber,
  activateEsim,
  reissueEsim,
  revokeEsim,
  topUpPhoneNumber,
  createPhoneNumberFromNft,
} from "../../../shared/api/phone";
import type { PhoneNumberStatus, TopUpPhoneNumberRequest } from "../../../shared/api/phone";
import type { PhoneNumber } from "../../../entities/phone-number";
import { mapPhoneNumberResponseToEntity } from "../../../entities/phone-number";
import { nftsKeys } from '../../nfts';
import { createOptimisticMutationCallbacks, createMultiEntityInvalidation } from "../../../shared/utils/query-helpers";

const phoneNumbersAllKey = ["phone-numbers"] as const;

export const phoneNumbersKeys = {
  all: phoneNumbersAllKey,
  list: (params: {
    walletId?: string | null;
    userId?: string | null;
    status?: PhoneNumberStatus | null;
    syncBalances?: boolean;
  }) =>
    [
      ...phoneNumbersAllKey,
      params.walletId ?? null,
      params.userId ?? null,
      params.status ?? null,
      params.syncBalances === true ? 'sync' : 'no-sync',
    ] as const,
  detail: (id: string) => ["phone-numbers", id] as const,
};

type PhoneNumbersQueryParams = {
  walletId?: string;
  userId?: string | null;
  status?: PhoneNumberStatus;
  syncBalances?: boolean;
  /**
   * If true, query will stay disabled until walletId is present.
   */
  requireWallet?: boolean;
  /**
   * If true, query will stay disabled until userId is present.
   */
  requireUser?: boolean;
};

export const usePhoneNumbersQuery = (
  params?: PhoneNumbersQueryParams,
  options?: Omit<UseQueryOptions<PhoneNumber[], Error>, 'queryKey' | 'queryFn'>,
) => {
  const enableByWallet = params?.requireWallet ? Boolean(params.walletId) : true;
  const enableByUser = params?.requireUser ? Boolean(params?.userId) : true;
  const isEnabled = enableByWallet && enableByUser;

  return useQuery<PhoneNumber[], Error>({
    queryKey: phoneNumbersKeys.list({
      walletId: params?.walletId ?? null,
      userId: params?.userId ?? null,
      status: params?.status ?? null,
      syncBalances: params?.syncBalances ?? false,
    }),
    queryFn: async () => {
      if (!params?.walletId) {
        throw new Error('Wallet ID is required for phone numbers query');
      }
      const response = await listPhoneNumbers({
        walletId: params.walletId,
        userId: params?.userId ?? undefined,
        syncBalances: params?.syncBalances,
      });

      let mapped = response.map(mapPhoneNumberResponseToEntity);
      if (params?.status) {
        mapped = mapped.filter((item) => item.status === params.status);
      }
      return mapped;
    },

    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: isEnabled && (options?.enabled ?? true),
    ...options,
  });
};

export const usePhoneNumberQuery = (
  phoneNumberId: string,
  walletId: string | undefined,
  options?: UseQueryOptions<PhoneNumber, Error>,
) =>
  useQuery<PhoneNumber, Error>({
    queryKey: phoneNumbersKeys.detail(phoneNumberId),
    queryFn: async () => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await getPhoneNumber(phoneNumberId, walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    enabled: !!phoneNumberId && !!walletId,
    ...options,
  });

export const useGeneratePhoneNumberMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, void>({
    mutationFn: async () => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await generatePhoneNumber(walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumbersKeys.all });
    },
  });
};

export const useActivateEsimMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, string>({
    mutationFn: async (phoneNumberId) => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await activateEsim(phoneNumberId, walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    ...createOptimisticMutationCallbacks(queryClient, phoneNumbersKeys),
  });
};

export const useReissueEsimMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, string>({
    mutationFn: async (phoneNumberId) => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await reissueEsim(phoneNumberId, walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    ...createOptimisticMutationCallbacks(queryClient, phoneNumbersKeys),
  });
};

export const useRevokeEsimMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, string>({
    mutationFn: async (phoneNumberId) => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await revokeEsim(phoneNumberId, walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    ...createOptimisticMutationCallbacks(queryClient, phoneNumbersKeys),
  });
};

export const useTopUpPhoneNumberMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  { phoneNumberId: string; data: TopUpPhoneNumberRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, { phoneNumberId: string; data: TopUpPhoneNumberRequest }>({
    mutationFn: async ({ phoneNumberId, data }) => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await topUpPhoneNumber(phoneNumberId, walletId, data);
      return mapPhoneNumberResponseToEntity(response);
    },
    ...createOptimisticMutationCallbacks(queryClient, phoneNumbersKeys),
  });
};

export const useCreatePhoneNumberFromNftMutation = (
  walletId: string | undefined,
): UseMutationResult<
  PhoneNumber,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation<PhoneNumber, unknown, string>({
    mutationFn: async (nftId) => {
      if (!walletId) {
        throw new Error('walletId is required');
      }
      const response = await createPhoneNumberFromNft(nftId, walletId);
      return mapPhoneNumberResponseToEntity(response);
    },
    onSuccess: (data) => {
      createMultiEntityInvalidation(queryClient, [
        [...phoneNumbersKeys.all],
        [...nftsKeys.base],
      ]).onSuccess();

      queryClient.invalidateQueries({ queryKey: phoneNumbersKeys.detail(data.id) });
      queryClient.setQueryData(phoneNumbersKeys.detail(data.id), data);
    },
  });
};

